import { EpgEvent, StreamInfo, StreamInfoType } from './StreamInfo';
import {
  calcEpgStartPosition,
  convertLivePlayerTimes,
  epgAnalyticsProperties,
  getEpgMetaInfo,
} from './StreamInfoUtility';
import { StreamType } from '../../../api/models/Shared.types';
import { Bookmark } from '../logicHandlers/Bookmark';
import { IBookmarkElementV1, IProperty } from '../../../api/models/AndromeTV.types';
import { Backend } from '../../../api/Backend';
import { timeStringToMs } from '../../utility/DateFormatter';

export class NpvrStreamInfo extends StreamInfo {
  private _epgEvent: EpgEvent;
  private _startPositionType: number;
  private _metaInfo: string;

  constructor(epgEvent: EpgEvent, bookmark: IBookmarkElementV1 | undefined, startPosition: number, backend: Backend) {
    super();
    this._epgEvent = epgEvent;
    this.bookmark = epgEvent.program
      ? Bookmark.fromProgram(epgEvent.program, bookmark ? bookmark.position : 0, epgEvent.series, backend)
      : undefined;
    this._startPositionType = startPosition;

    this._metaInfo = getEpgMetaInfo(backend, epgEvent.schedule?.published);
  }

  override get isAdult() {
    return this._epgEvent.program?.adult;
  }

  override get assetId() {
    return this._epgEvent.recording?.identifier;
  }

  override get resourceId() {
    return this._epgEvent.recording?.identifier;
  }

  override get type() {
    return StreamInfoType.NPVR;
  }

  override get titleText() {
    return this._epgEvent.program && this._epgEvent.program.text.title ? this._epgEvent.program.text.title : '';
  }

  override get metaInfo() {
    return this._metaInfo;
  }

  override get channelName() {
    return this._epgEvent.channel.text.title || '';
  }

  override get streamType() {
    return StreamType.NPVR;
  }

  override get startPositionTime() {
    if (this.bookmark && this.currentAsset) {
      return calcEpgStartPosition(
        this._startPositionType,
        this.currentAsset.event,
        this.bookmark.value.position,
        this.currentAsset.bookmarkOffset ?? 0,
        this.bookmark.endOffset
      );
    }
  }

  override get startOffset() {
    return (this.currentAsset ? this.currentAsset.bookmarkOffset || 0 : 0) / 1000;
  }

  override convertPlayTimes(pos: number, duration: number, isLive: boolean) {
    if (isLive && this.currentAsset?.event) {
      return convertLivePlayerTimes(pos, this.currentAsset?.event);
    } else {
      return super.convertPlayTimes(pos, duration, isLive);
    }
  }

  override updateBookmark(position: number, duration: number) {
    if (this._epgEvent.recording?.plannedEnd) {
      duration =
        timeStringToMs(this._epgEvent.recording.plannedEnd) - timeStringToMs(this._epgEvent.recording.plannedStart);
      // divide by 1000 to have both position and duration in seconds
      duration = duration / 1000;
    }
    this.bookmark?.updateTime(position - this.startOffset, duration);
    return true;
  }

  override get episode(): number {
    if (this._epgEvent.program) {
      return this._epgEvent.program.episodeNumber ?? 0;
    } else {
      return 0;
    }
  }

  override get season(): number {
    if (this._epgEvent.season) {
      return this._epgEvent.season.seasonNumber ?? 0;
    } else {
      return 0;
    }
  }

  override generateAnalyticsProperties(excluded: string[]): IProperty[] {
    const properties = super.generateAnalyticsProperties(excluded);
    properties.push(...epgAnalyticsProperties(this._epgEvent, excluded));
    return properties;
  }

  override updateStartPositionType(type: number) {
    this._startPositionType = type;
  }
}
